/**
 * Browser-side bridge to the Tavsin Web server. Mirrors the desktop renderer's
 * `invoke`, but over `POST /api/invoke` instead of an Electron channel — same
 * request/response envelope, so the same core dispatch answers both.
 *
 * Supports optional shared-token auth: when the server replies 401, callers can
 * set a bearer token (persisted in localStorage) and retry. The token, if any,
 * is attached to every request and the SSE stream.
 */
import type { PlanId } from "../plans.js";
import type { DraftFrame } from "./liveDraft.js";
const TOKEN_KEY = "tavsin.web.token";

export function getToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? "";
  } catch {
    return "";
  }
}
export function setToken(token: string): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

// ── active project (hosted multi-user) ───────────────────────────────────────
// Every request names the project it is for (`x-tavsin-project`); an iframe
// or a link names it with `?project=` instead. Empty = the server picks the
// user's most recently used project, which is also what a single-tenant
// server (one project) does with any request.
const PROJECT_KEY = "tavsin.web.project";
/** Raised on `window` when the server says the named project is gone. */
export const PROJECT_GONE_EVENT = "tavsin:project-gone";

export function getActiveProject(): string {
  try {
    return localStorage.getItem(PROJECT_KEY) ?? "";
  } catch {
    return "";
  }
}
export function setActiveProject(id: string): void {
  try {
    if (id) localStorage.setItem(PROJECT_KEY, id);
    else localStorage.removeItem(PROJECT_KEY);
  } catch {
    /* ignore */
  }
}

/** `url` with `project=<active>` added — for an iframe or link, which cannot set a header. */
export function withProjectParam(url: string): string {
  const p = getActiveProject();
  if (!p) return url;
  const hash = url.indexOf("#");
  const base = hash === -1 ? url : url.slice(0, hash);
  const frag = hash === -1 ? "" : url.slice(hash);
  return `${base}${base.includes("?") ? "&" : "?"}project=${encodeURIComponent(p)}${frag}`;
}

export function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = getToken();
  const project = getActiveProject();
  return {
    ...extra,
    ...(token ? { authorization: `Bearer ${token}` } : {}),
    ...(project ? { "x-tavsin-project": project } : {}),
  };
}

/** The named project no longer exists (deleted in another tab): forget it and say so. */
function projectGone(): void {
  setActiveProject("");
  try {
    window.dispatchEvent(new Event(PROJECT_GONE_EVENT));
  } catch {
    /* not in a browser */
  }
}

export interface WebProjectView {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string;
}
export interface ProjectListView {
  projects: WebProjectView[];
  active: string;
  max: number;
  /** False on a single-tenant server: one project, chosen by the operator. */
  multi: boolean;
}

async function projectsCall<T>(method: "GET" | "POST" | "PATCH" | "DELETE", path: string, body?: Record<string, unknown>): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: authHeaders(body ? { "content-type": "application/json" } : {}),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 401) throw new AuthError();
  const out = (await res.json().catch(() => ({ ok: false, error: `HTTP ${String(res.status)}` }))) as { ok: boolean; data?: T; error?: string };
  if (!out.ok) throw new Error(out.error ?? "request failed");
  return out.data as T;
}
export const listProjects = (): Promise<ProjectListView> => projectsCall<ProjectListView>("GET", "/api/projects");
export const createProject = (name: string): Promise<{ project: WebProjectView }> =>
  projectsCall<{ project: WebProjectView }>("POST", "/api/projects", { name });
export const renameProject = (id: string, name: string): Promise<{ project: WebProjectView }> =>
  projectsCall<{ project: WebProjectView }>("PATCH", `/api/projects/${encodeURIComponent(id)}`, { name });
export const deleteProject = (id: string): Promise<{ deleted: string; active: string }> =>
  projectsCall<{ deleted: string; active: string }>("DELETE", `/api/projects/${encodeURIComponent(id)}`);
export interface TrashedProjectView {
  key: string;
  id: string;
  name: string;
  deletedAt: string;
}
export const listProjectTrash = (): Promise<{ trash: TrashedProjectView[] }> => projectsCall<{ trash: TrashedProjectView[] }>("GET", "/api/projects/trash");
export const restoreProject = (key: string): Promise<{ project: WebProjectView }> =>
  projectsCall<{ project: WebProjectView }>("POST", `/api/projects/trash/${encodeURIComponent(key)}/restore`);

// A crashing page can throw in a render loop, so dedupe by message+stack and cap
// the total per page load. The server rate-limits too; this just keeps us polite.
const reportedErrors = new Set<string>();
let errorReportCount = 0;

/**
 * Report a browser crash to the operator's error log. Fire-and-forget: it must
 * never throw, and never block or surface to the user.
 */
export function reportClientError(message: string, stack?: string): void {
  if (!message.trim() || errorReportCount >= 10) return;
  const key = `${message}::${(stack ?? "").slice(0, 200)}`;
  if (reportedErrors.has(key)) return;
  reportedErrors.add(key);
  errorReportCount += 1;
  void fetch("/api/client-error", {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ message, ...(stack ? { stack } : {}), url: location.href }),
  }).catch(() => undefined);
}

export class AuthError extends Error {
  constructor() {
    super("unauthorized");
    this.name = "AuthError";
  }
}

export interface AuthInfo {
  /** True ⇔ server requires auth before answering `/api/invoke`. */
  required: boolean;
  /** "open" (anonymous), "token" (static bearer), "password" (owner), "multiuser" (signup). */
  mode: "open" | "token" | "password" | "multiuser";
  /** Multi-user signup is enabled. */
  allowsSignup: boolean;
  /** Sign-in providers the operator has configured, in display order. */
  oauthProviders?: SignInProvider[];
  providers?: Array<{ id: SignInProvider; label: string }>;
  /** Passkeys can be used on this server (multi-user mode). */
  passkeys?: boolean;
  /** One-time email codes are offered (the server can send mail). */
  emailCode?: boolean;
}

export type SignInProvider = "google" | "apple" | "microsoft" | "github" | "chatgpt";

export interface PublicUser {
  id: string;
  email: string;
  displayName: string | null;
  tier: "local" | PlanId | "enterprise";
  emailVerified: boolean;
  /** ISO time the account was made. Absent from servers older than this field. */
  createdAt?: string;
}

export async function fetchAuthInfo(): Promise<AuthInfo> {
  const res = await fetch("/api/auth/info");
  const body = (await res.json()) as { ok: boolean; data: AuthInfo };
  return body.data;
}

export async function fetchMe(): Promise<PublicUser | null> {
  const res = await fetch("/api/auth/me", { headers: authHeaders() });
  if (res.status === 401) return null;
  const body = (await res.json()) as { ok: boolean; data?: { user: PublicUser } };
  return body.ok ? body.data?.user ?? null : null;
}

// ── dev-sandbox preview (flag-gated on the server) ─────────────────────────
export interface DevSandboxStatus { available: boolean; running: boolean; url: string | null }

/** Status of the live dev-server preview. `available:false` when the server
 *  has the feature disabled (404) — the UI hides the control in that case. */
export async function devSandboxStatus(): Promise<DevSandboxStatus> {
  const res = await fetch("/api/preview/sandbox", { headers: authHeaders() }).catch(() => null);
  if (!res || res.status === 404) return { available: false, running: false, url: null };
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; data?: { running?: boolean; url?: string | null } };
  return { available: true, running: Boolean(body.data?.running), url: body.data?.url ?? null };
}

/** Start (or reuse) the live dev server. Returns the proxied URL, or an error
 *  reason ("disabled" | "tier" | "capacity" | the message). */
export async function startDevSandbox(command?: string): Promise<{ url: string } | { error: string }> {
  const res = await fetch("/api/preview/sandbox", {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(command ? { command } : {}),
  }).catch(() => null);
  if (!res) return { error: "network" };
  if (res.status === 404) return { error: "disabled" };
  if (res.status === 402) return { error: "tier" };
  if (res.status === 503) return { error: "capacity" };
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; data?: { url?: string }; error?: string };
  return body.ok && body.data?.url ? { url: body.data.url } : { error: body.error ?? "start failed" };
}

export async function stopDevSandbox(): Promise<void> {
  await fetch("/api/preview/sandbox", { method: "DELETE", headers: authHeaders() }).catch(() => undefined);
}

export async function loginWithPassword(password: string): Promise<string> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ password }),
  });
  const body = (await res.json()) as { ok: boolean; data?: { token: string }; error?: string };
  if (!body.ok) throw new Error(body.error ?? "login failed");
  return body.data!.token;
}

export async function loginWithEmail(email: string, password: string): Promise<{ token: string; user: PublicUser }> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const body = (await res.json()) as { ok: boolean; data?: { token: string; user: PublicUser }; error?: string };
  if (!body.ok) throw new Error(body.error ?? "login failed");
  return body.data!;
}

/** A refusal from the sign-in API, with the server's extra flags kept. */
export class SignInApiError extends Error {
  constructor(message: string, readonly status: number, readonly flags: { reauth?: boolean; needsAgeConfirmation?: boolean; ssoUrl?: string }) {
    super(message);
    this.name = "SignInApiError";
  }
}

async function authCall<T>(method: string, path: string, body?: unknown, signedIn = false): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: signedIn ? authHeaders({ "content-type": "application/json" }) : { "content-type": "application/json" },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = (await res.json().catch(() => ({}))) as { ok?: boolean; data?: T; error?: string; reauth?: boolean; needsAgeConfirmation?: boolean; ssoUrl?: string };
  if (!res.ok || !json.ok) {
    throw new SignInApiError(json.error ?? `request failed (HTTP ${String(res.status)})`, res.status, {
      ...(json.reauth ? { reauth: true } : {}),
      ...(json.needsAgeConfirmation ? { needsAgeConfirmation: true } : {}),
      ...(json.ssoUrl ? { ssoUrl: json.ssoUrl } : {}),
    });
  }
  return json.data as T;
}

/** Mail a one-time sign-in code. Always "sent" — the server never says whether the account exists. */
export function startEmailCode(email: string): Promise<{ sent: boolean }> {
  return authCall("POST", "/api/auth/email/start", { email });
}
export function verifyEmailCode(input: { email: string; code: string; ageConfirmed?: boolean; displayName?: string }): Promise<{ token: string; user: PublicUser; created: boolean }> {
  return authCall("POST", "/api/auth/email/verify", input);
}

export function passkeyLoginOptions(): Promise<Record<string, unknown>> {
  return authCall("POST", "/api/auth/passkey/login/options", {});
}
export function passkeyLoginVerify(credential: Record<string, unknown>): Promise<{ token: string; user: PublicUser }> {
  return authCall("POST", "/api/auth/passkey/login/verify", { credential });
}
export function passkeyRegisterOptions(): Promise<Record<string, unknown>> {
  return authCall("POST", "/api/auth/passkey/register/options", {}, true);
}
export function passkeyRegisterVerify(credential: Record<string, unknown>, name: string): Promise<{ id: string; name: string }> {
  return authCall("POST", "/api/auth/passkey/register/verify", { credential, name }, true);
}

export interface SignInMethods {
  password: boolean;
  identities: Array<{ provider: SignInProvider; email: string | null; createdAt: string; lastUsedAt: string }>;
  passkeys: Array<{ id: string; name: string; createdAt: string; lastUsedAt: string | null }>;
  available: Array<{ id: SignInProvider; label: string }>;
  /** False when the session is too old to change sign-in methods. */
  recent: boolean;
}
export function fetchSignInMethods(): Promise<SignInMethods> {
  return authCall("GET", "/api/auth/methods", undefined, true);
}
export function linkProvider(provider: SignInProvider): Promise<{ url: string }> {
  return authCall("POST", `/api/auth/oauth/${provider}/link`, {}, true);
}
export function unlinkProvider(provider: SignInProvider): Promise<{ removed: boolean }> {
  return authCall("DELETE", `/api/auth/identities/${provider}`, undefined, true);
}
export function removePasskey(id: string): Promise<{ removed: boolean }> {
  return authCall("DELETE", `/api/auth/passkeys/${encodeURIComponent(id)}`, undefined, true);
}
export function renamePasskey(id: string, name: string): Promise<{ renamed: boolean }> {
  return authCall("PATCH", `/api/auth/passkeys/${encodeURIComponent(id)}`, { name }, true);
}

/** Exchange the one-time OAuth handoff code (from ?oauth=) for a session token. */
export async function exchangeOAuth(code: string): Promise<{ token: string; user: PublicUser } | null> {
  const res = await fetch("/api/auth/oauth/exchange", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code }),
  });
  const body = (await res.json()) as { ok: boolean; data?: { token: string; user: PublicUser } };
  return body.ok ? body.data ?? null : null;
}

/**
 * Create an account. `ageConfirmed` is the person's own "I am at least 13"
 * attestation (COPPA / GDPR-K); the server refuses a signup without it, so the
 * form must collect it — sending it unasked would be a false attestation.
 */
export async function signup(email: string, password: string, displayName: string | undefined, ageConfirmed: boolean): Promise<{ token: string; user: PublicUser }> {
  const res = await fetch("/api/auth/signup", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password, ageConfirmed, ...(displayName ? { displayName } : {}) }),
  });
  const body = (await res.json()) as { ok: boolean; data?: { token: string; user: PublicUser }; error?: string };
  if (!body.ok) throw new Error(body.error ?? "signup failed");
  return body.data!;
}

export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST", headers: authHeaders() }).catch(() => undefined);
  setToken("");
}

// ── verify / reset / billing ─────────────────────────────────────────────────
export async function verifyEmail(token: string): Promise<void> {
  const res = await fetch("/api/auth/verify", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
  });
  const body = (await res.json()) as { ok: boolean; error?: string };
  if (!body.ok) throw new Error(body.error ?? "verification failed");
}

export async function requestPasswordReset(email: string): Promise<void> {
  const res = await fetch("/api/auth/reset/request", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const body = (await res.json()) as { ok: boolean; error?: string };
  if (!body.ok) throw new Error(body.error ?? "could not send reset email");
}

export async function confirmPasswordReset(token: string, password: string): Promise<string> {
  const res = await fetch("/api/auth/reset/confirm", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  const body = (await res.json()) as { ok: boolean; data?: { token: string }; error?: string };
  if (!body.ok) throw new Error(body.error ?? "reset failed");
  return body.data!.token;
}

export interface BillingInfo {
  enabled: boolean;
  /** Plans with a monthly price configured. */
  plans: PlanId[];
  /** Plans that also have a yearly price. */
  yearlyPlans?: PlanId[];
}
export async function fetchBillingInfo(): Promise<BillingInfo> {
  const res = await fetch("/api/billing/info", { headers: authHeaders() });
  const body = (await res.json()) as { ok: boolean; data?: BillingInfo };
  return body.data ?? { enabled: false, plans: [] };
}

// ── Launched sites (server sites.ts) ───────────────────────────────────────
export interface SiteVersionView { id: string; publishedAt: string; files: number; bytes: number; source: string; digest: string }
/** One DNS record the owner creates for a custom domain (server domains.ts). */
export interface DnsRecordView { type: "TXT" | "CNAME" | "A" | "AAAA"; name: string; value: string }
export interface SiteDomainView {
  domain: string;
  status: "pending" | "verified" | "failed";
  url: string;
  addedAt: string;
  verifiedAt?: string;
  checkedAt?: string;
  lastError?: string;
  /** A connected domain whose DNS checks have failed since then; released after 7 days of that. */
  failingSince?: string;
  records: DnsRecordView[];
  note: string;
}
export interface SiteView { slug: string; url: string; current: string | null; createdAt: string; versions: SiteVersionView[]; domains?: SiteDomainView[] }
export interface SiteDomainsInfo { enabled: boolean; limit: number; used: number; target: string | null; ips: string[] }
export interface SitesInfo { sites: SiteView[]; limits: { maxSites: number; maxBytes: number; maxFiles: number }; host: string | null; domains?: SiteDomainsInfo }

async function sitesCall<T>(path: string, body?: Record<string, unknown>): Promise<T> {
  const res = await fetch(path, body === undefined
    ? { headers: authHeaders() }
    : { method: "POST", headers: authHeaders({ "content-type": "application/json" }), body: JSON.stringify(body) });
  if (res.status === 401) throw new AuthError();
  const out = (await res.json().catch(() => ({ ok: false, error: `HTTP ${String(res.status)}` }))) as { ok: boolean; data?: T; error?: string };
  if (!out.ok) throw new Error(out.error ?? "request failed");
  return out.data as T;
}
export const listSites = (): Promise<SitesInfo> => sitesCall<SitesInfo>("/api/sites");
/** One finding of the server's pre-launch site check (core design/siteCheck.ts). */
export interface SiteFindingView { rule: string; severity: "block" | "warn"; file: string; line?: number; message: string }
/** The launch was refused by the site check; `findings` says why, and `force` launches anyway. */
export class SitePreflightError extends Error {
  constructor(message: string, readonly findings: SiteFindingView[]) {
    super(message);
    this.name = "SitePreflightError";
  }
}
export async function publishSite(slug: string, force = false): Promise<{ site: SiteView; version: SiteVersionView; skipped: string[]; warnings?: SiteFindingView[] }> {
  const res = await fetch("/api/sites/publish", { method: "POST", headers: authHeaders({ "content-type": "application/json" }), body: JSON.stringify(force ? { slug, force: true } : { slug }) });
  if (res.status === 401) throw new AuthError();
  const out = (await res.json().catch(() => ({ ok: false, error: `HTTP ${String(res.status)}` }))) as { ok: boolean; data?: { site: SiteView; version: SiteVersionView; skipped: string[]; warnings?: SiteFindingView[] }; error?: string; findings?: SiteFindingView[] };
  if (!out.ok || !out.data) {
    if (Array.isArray(out.findings)) throw new SitePreflightError(out.error ?? "the site check refused the launch", out.findings);
    throw new Error(out.error ?? "request failed");
  }
  return out.data;
}
export const rollbackSite = (slug: string, versionId: string): Promise<{ site: SiteView }> => sitesCall("/api/sites/rollback", { slug, versionId });
export const unpublishSite = (slug: string): Promise<{ slug: string }> => sitesCall("/api/sites/unpublish", { slug });
export const addSiteDomain = (slug: string, domain: string): Promise<{ domain: SiteDomainView; records: DnsRecordView[]; note: string }> =>
  sitesCall(`/api/sites/${encodeURIComponent(slug)}/domains`, { domain });
export const verifySiteDomain = (slug: string, domain: string): Promise<{ domain: SiteDomainView }> =>
  sitesCall(`/api/sites/${encodeURIComponent(slug)}/domains/${encodeURIComponent(domain)}/verify`, {});
/** Records a launched site (or this project's preview) stored via /_data/ (siteData.ts). */
export interface SiteDataRecordView { id: string; createdAt: string; data: Record<string, string | number | boolean> }
export interface SiteDataView {
  spec: { collections: Record<string, { public: string[]; fields: Record<string, { type: string; required?: boolean }> }> } | null;
  configError: string | null;
  counts: Record<string, number>;
  collection: string;
  records: SiteDataRecordView[];
}
export type SiteDataSource = { slug: string } | { preview: true };
const dataQuery = (src: SiteDataSource, collection?: string, extra = ""): string =>
  `/api/sites/data?${"slug" in src ? `slug=${encodeURIComponent(src.slug)}` : "preview=1"}${collection ? `&collection=${encodeURIComponent(collection)}` : ""}${extra}`;
export const getSiteData = (src: SiteDataSource, collection?: string): Promise<SiteDataView> => sitesCall<SiteDataView>(dataQuery(src, collection));
export const deleteSiteRecord = (src: SiteDataSource, collection: string, id: string): Promise<{ deleted: boolean }> =>
  sitesCall("/api/sites/data/delete", { ...("slug" in src ? { slug: src.slug } : { preview: true }), collection, id });
/** A collection as CSV (the browser saves it). */
export async function exportSiteData(src: SiteDataSource, collection: string): Promise<Blob> {
  const res = await fetch(dataQuery(src, collection, "&format=csv"), { headers: authHeaders() });
  if (res.status === 401) throw new AuthError();
  if (!res.ok) throw new Error(`HTTP ${String(res.status)}`);
  return res.blob();
}
export async function removeSiteDomain(slug: string, domain: string): Promise<void> {
  const res = await fetch(`/api/sites/${encodeURIComponent(slug)}/domains/${encodeURIComponent(domain)}`, { method: "DELETE", headers: authHeaders() });
  if (res.status === 401) throw new AuthError();
  const out = (await res.json().catch(() => ({ ok: false, error: `HTTP ${String(res.status)}` }))) as { ok: boolean; error?: string };
  if (!out.ok) throw new Error(out.error ?? "request failed");
}

// ── Phone notifications (server push.ts) ──────────────────────────────────
export interface PushDeviceView { endpoint: string; label: string; createdAt: string }
export interface PushStatusView { publicKey: string; devices: PushDeviceView[]; prefs: { turns: boolean; needsYou: boolean } }
export const pushStatus = (): Promise<PushStatusView> => sitesCall<PushStatusView>("/api/push/status");
export const pushSubscribe = (subscription: unknown): Promise<{ devices: number }> => sitesCall("/api/push/subscribe", { subscription });
export const pushUnsubscribe = (endpoint: string): Promise<{ devices: number }> => sitesCall("/api/push/unsubscribe", { endpoint });
export const pushPrefs = (patch: { turns?: boolean; needsYou?: boolean }): Promise<{ turns: boolean; needsYou: boolean }> => sitesCall("/api/push/prefs", patch);
export const pushTest = (): Promise<{ sent: number; removed: number }> => sitesCall("/api/push/test", {});

export async function startCheckout(plan: PlanId, quantity?: number, interval: "month" | "year" = "month"): Promise<string> {
  const res = await fetch("/api/billing/checkout", {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ plan, interval, ...(quantity ? { quantity } : {}) }),
  });
  const body = (await res.json()) as { ok: boolean; data?: { url: string }; error?: string };
  if (!body.ok) throw new Error(body.error ?? "checkout failed");
  return body.data!.url;
}

// ── teams ────────────────────────────────────────────────────────────────────
export interface Team { id: string; name: string; ownerUserId: string; createdAt: string; }
export interface TeamMember { userId: string; role: "owner" | "member"; joinedAt: string; email: string | null; displayName: string | null; }
export interface TeamMemory { id: string; teamId: string; kind: string; title: string; content: string; proposedBy: string; proposedAt: string; approvalStatus: "proposed" | "approved" | "rejected"; approvedBy: string | null; approvedAt: string | null; }

export async function listTeams(): Promise<{ teams: Team[]; canCreate: boolean }> {
  const res = await fetch("/api/teams", { headers: authHeaders() });
  if (res.status === 401) throw new AuthError();
  const body = (await res.json()) as { ok: boolean; data?: { teams: Team[]; canCreate: boolean } };
  return body.data ?? { teams: [], canCreate: false };
}
export async function createTeam(name: string): Promise<Team> {
  const res = await fetch("/api/teams", { method: "POST", headers: authHeaders({ "content-type": "application/json" }), body: JSON.stringify({ name }) });
  const body = (await res.json()) as { ok: boolean; data?: { team: Team }; error?: string };
  if (!body.ok) throw new Error(body.error ?? "create team failed");
  return body.data!.team;
}
export async function listTeamMembers(teamId: string): Promise<TeamMember[]> {
  const res = await fetch(`/api/teams/${encodeURIComponent(teamId)}/members`, { headers: authHeaders() });
  const body = (await res.json()) as { ok: boolean; data?: { members: TeamMember[] } };
  return body.data?.members ?? [];
}
export async function inviteToTeam(teamId: string, email: string): Promise<{ url: string }> {
  const res = await fetch(`/api/teams/${encodeURIComponent(teamId)}/invites`, { method: "POST", headers: authHeaders({ "content-type": "application/json" }), body: JSON.stringify({ email }) });
  const body = (await res.json()) as { ok: boolean; data?: { url: string }; error?: string };
  if (!body.ok) throw new Error(body.error ?? "invite failed");
  return body.data!;
}
export async function acceptTeamInvite(token: string): Promise<Team> {
  const res = await fetch("/api/teams/accept-invite", { method: "POST", headers: authHeaders({ "content-type": "application/json" }), body: JSON.stringify({ token }) });
  const body = (await res.json()) as { ok: boolean; data?: { team: Team }; error?: string };
  if (!body.ok) throw new Error(body.error ?? "accept failed");
  return body.data!.team;
}
export async function listTeamMemories(teamId: string): Promise<TeamMemory[]> {
  const res = await fetch(`/api/teams/${encodeURIComponent(teamId)}/memories`, { headers: authHeaders() });
  const body = (await res.json()) as { ok: boolean; data?: { memories: TeamMemory[] } };
  return body.data?.memories ?? [];
}
export async function proposeTeamMemory(teamId: string, content: string, title?: string): Promise<TeamMemory> {
  const res = await fetch(`/api/teams/${encodeURIComponent(teamId)}/memories`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ content, ...(title ? { title } : {}) }),
  });
  const body = (await res.json()) as { ok: boolean; data?: { memory: TeamMemory }; error?: string };
  if (!body.ok) throw new Error(body.error ?? "propose failed");
  return body.data!.memory;
}
export async function decideTeamMemory(teamId: string, memoryId: string, decision: "approve" | "reject"): Promise<TeamMemory> {
  const res = await fetch(`/api/teams/${encodeURIComponent(teamId)}/memories/${encodeURIComponent(memoryId)}/${decision}`, {
    method: "POST",
    headers: authHeaders(),
  });
  const body = (await res.json()) as { ok: boolean; data?: { memory: TeamMemory }; error?: string };
  if (!body.ok) throw new Error(body.error ?? "decide failed");
  return body.data!.memory;
}

export async function openBillingPortal(): Promise<string> {
  const res = await fetch("/api/billing/portal", {
    method: "POST",
    headers: authHeaders(),
  });
  const body = (await res.json()) as { ok: boolean; data?: { url: string }; error?: string };
  if (!body.ok) throw new Error(body.error ?? "portal failed");
  return body.data!.url;
}

export interface IpcEnvelope<T> {
  ok: boolean;
  data?: T;
  error?: string;
  /** Stable refusal code, e.g. PROJECT_NOT_FOUND. */
  code?: string;
}

export async function invoke<T>(channel: string, payload?: unknown): Promise<T> {
  const res = await fetch("/api/invoke", {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ channel, payload }),
  });
  if (res.status === 401) throw new AuthError();
  const env = (await res.json()) as IpcEnvelope<T>;
  if (!env.ok && env.code === "PROJECT_NOT_FOUND") projectGone();
  if (!env.ok) throw new Error(env.error ?? "request failed");
  return env.data as T;
}

/** Evidence-derived, never prose: read off the turn's own trace by core. */
export interface TurnVerificationReply {
  status: "verified" | "failing" | "unverified" | "ran";
  checks: { command: string; ok: boolean; exitCode: number; verify: boolean }[];
  tests: { command: string; passed: number; failed: number; skipped: number } | null;
  editsAfterCheck: number;
  /** Finish gates that REFUSED during this turn. */
  gates: { kind: string; count: number }[];
}

/** Rolled-up model_call rows for one chat trace (`traces.usageReceipt`). */
export interface TraceUsageReceiptView {
  traceId: string;
  callCount: number;
  tokensIn: number;
  tokensOut: number;
  totalUsd: number;
  unpricedCalls: number;
}

export interface ChatReply {
  reply: string;
  provider: string;
  model: string;
  costUsd: number | null;
  /** The trace this turn ran under — what a later undo is recorded against. */
  traceId?: string;
  /** Absent on an answer-only turn — nothing was observed, so nothing is claimed. */
  verification?: TurnVerificationReply | null;
  decisions?: TurnDecisionView[];
  /** Policy refusals and a budget pause, read back from the trace (core `chat/policyNotes.ts`). */
  policy?: TurnPolicyView;
  /** MEASURED confidence (core `chat/confidence.ts`) — the answer's own claim can only lower it. */
  confidence?: TurnConfidenceView | null;
  /** Questions the agent asked this turn, and the answer or default it used. */
  questions?: SettledQuestionView[];
  /** Mid-turn messages the agent set aside as separate tasks — the SPA runs each next. */
  deferred?: string[];
  /** Mid-turn messages that arrived as the turn closed — the SPA runs them next. */
  undelivered?: string[];
  /** Teammates' messages that arrived as the turn closed — shown to the owner, never run. */
  teammate?: { from: string; text: string }[];
}

/** Mirrors core's TurnConfidence. */
export interface TurnConfidenceView {
  level: "high" | "medium" | "low";
  score: number;
  reasons: string[];
  claimed: "high" | "medium" | "low" | null;
  capped: boolean;
  sources: { filesRead: number; urlsRetrieved: number; checksPassed: number };
  /** For a change: unit → e2e → target, observed after the last edit. 100 needs all three. */
  ladder?: VerificationLadderView;
}

/** Mirrors core's VerificationLadder. */
export interface VerificationLadderView {
  aim: "ios" | "android" | "web" | "api" | "cli" | null;
  full: boolean;
  staticPassed: string[];
  rungs: { layer: "unit" | "e2e" | "target"; status: "passed" | "partial" | "failed" | "not_run" | "unavailable"; detail: string }[];
}

/** A question the agent asked (ask.user) and how it was settled. */
export interface SettledQuestionView {
  question: string;
  why: string;
  options: string[];
  default: string;
  answer: string;
  answeredBy: "user" | "default";
}

/** The agent's question while it runs: "asked" while it waits, then "answered" / "assumed". */
export interface QuestionFrame {
  t: "question";
  id: string;
  question: string;
  why: string;
  options: string[];
  default: string;
  waitMs: number;
  phase: "asked" | "answered" | "assumed";
  answer: string | null;
}

/** Mirrors core's UserProfile. */
export interface UserProfileView {
  about: string;
  preferences: { askPolicy: "never" | "rarely" | "when_unsure"; verbosity: "brief" | "normal" | "detailed"; autonomy?: "normal" | "full"; notes: string[] };
  observed: { turns: number; changeTurns: number; fullyVerified: number; confidenceSum: number; languages: Record<string, number>; fields: Record<string, number>; checks: Record<string, number>; targets: Record<string, number>; updatedAt: string | null };
  lessons: { id: string; text: string; source: "answer" | "correction" | "critique" | "revert" | "stated"; count: number; firstAt: string; lastAt: string }[];
}

/**
 * A request refused for a LIMIT rather than a fault: too many tasks at once,
 * a rate limit, a plan feature, or spent credits. Its message is written for
 * the person reading it, and `kind` lets the caller offer the fix.
 */
/** What a stopped or failed turn never got to (core TurnHandback). */
export interface TurnHandback {
  deferred: string[];
  undelivered: string[];
  teammate: { from: string; text: string }[];
}

/** A turn that ended without a reply: stopped, or failed. Carries its hand-back. */
export class TurnEndedError extends Error {
  constructor(message: string, readonly stopped: boolean, readonly handback: TurnHandback | null, readonly code?: string) {
    super(message);
    this.name = "TurnEndedError";
  }
}

/** The hand-back an error carries, whichever kind of error it is. */
export function handbackOf(e: unknown): TurnHandback | null {
  return e instanceof TurnEndedError || e instanceof LimitError ? e.handback : null;
}

export class LimitError extends Error {
  handback: TurnHandback | null = null;
  constructor(message: string, readonly kind: "rate" | "plan" | "credits", readonly retryAfterSec: number | null = null) {
    super(message);
    this.name = "LimitError";
  }
}

/** A tool call the policy engine refused this turn. */
export interface PolicyBlockView {
  tool: string;
  rule: string;
  reason: string;
  target: string | null;
}
/** Mirrors desktop's TurnPolicyView — the same `conversations.get` / reply field. */
export interface TurnPolicyView {
  blocks: PolicyBlockView[];
  paused: { reason: "budget"; scope: "month" | "run"; spentUsd: number; budgetUsd: number } | null;
}

export type StreamEvent =
  | { t: "delta"; text: string }
  | { t: "done"; reply: ChatReply }
  | { t: "error"; error: string; code?: string; aborted?: boolean; handback?: TurnHandback }
  // Live policy events, so the chip appears while the turn is still streaming.
  | { t: "blocked"; tool: string; reason: string; rule: string | null }
  | { t: "paused"; reason: "budget"; scope: "month" | "run"; spentUsd: number; budgetUsd: number }
  // A teammate watching a live share steered this turn (apps/web liveShare).
  | { t: "steer"; by: string; text: string }
  // A step's live draft — provisional text, never the answer (see liveDraft.ts).
  | DraftFrame
  // The agent asked the user something (ask.user).
  | QuestionFrame;

/** What a live policy event does to the turn being streamed. */
export type LivePolicyEvent = Extract<StreamEvent, { t: "blocked" } | { t: "paused" }>;

/**
 * Stream an agentic chat turn over SSE. Calls `onDelta` for each token chunk and
 * resolves with the final reply. Returns an `abort` fn so the caller can stop.
 */
let streamCounter = 0;

export function streamChat(
  payload: Record<string, unknown>,
  onDelta: (text: string) => void,
  onPolicy?: (e: LivePolicyEvent) => void,
  onTeammateSteer?: (by: string, text: string) => void,
  onDraft?: (frame: DraftFrame) => void,
  onQuestion?: (frame: QuestionFrame) => void,
): { done: Promise<ChatReply>; abort: () => void; steer: (text: string) => Promise<boolean> } {
  // Stop asks the SERVER to stop the turn and keeps the stream open for its
  // last frame, which hands back what the turn never got to. Only if that
  // request fails (or the frame never comes) is the connection dropped.
  let stopRequested = false;
  const controller = new AbortController();
  // Names the stream so a follow-up typed mid-turn can be steered INTO it.
  streamCounter += 1;
  const streamId = `web-${String(Date.now())}-${String(streamCounter)}`;
  const done = (async (): Promise<ChatReply> => {
    const res = await fetch("/api/chat/stream", {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ ...payload, streamId }),
      signal: controller.signal,
    });
    if (res.status === 401) throw new AuthError();
    // A refusal arrives as JSON, not SSE — read it instead of reporting
    // "stream ended without a result" for what is really a limit.
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; upgrade?: string; reason?: string };
      if (res.status === 429) {
        const retry = Number(res.headers.get("retry-after") ?? "") || null;
        throw new LimitError(`Too many tasks at once${retry ? ` — try again in ${String(retry)}s` : ""}. Tasks you send now are queued and run one after another.`, "rate", retry);
      }
      if (res.status === 402) throw new LimitError(body.error ?? "This needs a paid plan.", body.upgrade ? "plan" : "credits");
      throw new Error(body.error ?? `request failed (HTTP ${String(res.status)})`);
    }
    if (!res.body) throw new Error("no stream");
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let final: ChatReply | null = null;
    let error: string | null = null;
    let errorCode: string | undefined;
    let handback: TurnHandback | null = null;
    let aborted = false;
    try {
      for (;;) {
        const { value, done: streamDone } = await reader.read();
        if (streamDone) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() ?? "";
        for (const frame of frames) {
          const line = frame.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          const evt = JSON.parse(line.slice(6)) as StreamEvent;
          if (evt.t === "delta") onDelta(evt.text);
          else if (evt.t === "done") final = evt.reply;
          else if (evt.t === "error") { error = evt.error; errorCode = evt.code; handback = evt.handback ?? null; aborted = evt.aborted === true; }
          else if (evt.t === "blocked" || evt.t === "paused") onPolicy?.(evt);
          else if (evt.t === "steer") onTeammateSteer?.(evt.by, evt.text);
          else if (evt.t === "draft" || evt.t === "draft_end") onDraft?.(evt);
          else if (evt.t === "question") onQuestion?.(evt);
        }
      }
    } catch (e) {
      // A frame that would not parse, or a handler that threw: release the
      // stream (the server then stops the turn) instead of leaving it locked
      // and still streaming into a page that has stopped reading.
      void reader.cancel().catch(() => undefined);
      throw e;
    }
    if (error && errorCode === "QUOTA_EXCEEDED") {
      const le = new LimitError(error, "credits");
      le.handback = handback;
      throw le;
    }
    if (error) throw new TurnEndedError(aborted || stopRequested ? "stopped" : error, aborted || stopRequested, handback, errorCode);
    if (!final) throw new Error("stream ended without a result");
    return final;
  })();
  const abort = (): void => {
    if (stopRequested) { controller.abort(); return; }
    stopRequested = true;
    void stopChat(streamId).then((ok) => {
      if (!ok) controller.abort();
      // The server's last frame should arrive within moments of a stop; if it
      // does not, the connection is dropped so Stop never hangs.
      else setTimeout(() => controller.abort(), 8_000);
    });
  };
  return { done, abort, steer: (text) => steerChat(streamId, text) };
}

/** Ask the server to stop a running turn. False when it could not (the caller then drops the stream). */
export async function stopChat(streamId: string): Promise<boolean> {
  try {
    const res = await fetch("/api/chat/stop", { method: "POST", headers: authHeaders({ "content-type": "application/json" }), body: JSON.stringify({ streamId }) });
    const body = (await res.json().catch(() => ({}))) as { data?: { stopped?: boolean } };
    return res.ok && body.data?.stopped === true;
  } catch {
    return false;
  }
}

/**
 * Inject a message into a running turn at its next model-call boundary.
 * Resolves false when the turn already ended, so the caller sends it fresh.
 */
export async function steerChat(streamId: string, text: string): Promise<boolean> {
  try {
    const res = await fetch("/api/chat/steer", {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ streamId, text }),
    });
    if (!res.ok) return false;
    const body = (await res.json()) as { ok?: boolean; data?: { accepted?: boolean } };
    return body.ok === true && body.data?.accepted === true;
  } catch {
    return false;
  }
}

// ── Shared view types (mirror the core/desktop shapes the channels return) ────
// ── Live shared sessions (Team tier, hosted web only) ────────────────────
export type LiveShareScope = "view" | "steer";
export interface LiveShareView { id: string; teamId: string; conversationId: string; scope: LiveShareScope; createdAt: string; expiresAt: string; revoked: boolean; viewers: number }
export interface CreatedLiveShare { id: string; token: string; url: string; scope: LiveShareScope; teamId: string; conversationId: string; expiresAt: string }

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, { method: "POST", headers: authHeaders({ "content-type": "application/json" }), body: JSON.stringify(body) });
  if (res.status === 401) throw new AuthError();
  const json = (await res.json()) as { ok: boolean; data?: T; error?: string };
  if (!json.ok || json.data === undefined) throw new Error(json.error ?? `request failed (${String(res.status)})`);
  return json.data;
}

export function createLiveShare(input: { conversationId: string; teamId: string; scope: LiveShareScope; ttlMinutes?: number }): Promise<CreatedLiveShare> {
  return postJson("/api/sessions/share/create", input);
}
export async function listLiveShares(conversationId?: string): Promise<LiveShareView[]> {
  return (await postJson<{ shares: LiveShareView[] }>("/api/sessions/share/list", conversationId ? { conversationId } : {})).shares;
}
export async function revokeLiveShare(id: string): Promise<void> {
  await postJson("/api/sessions/share/revoke", { id });
}

export type LiveViewEvent =
  | { t: "hello"; share: { scope: LiveShareScope; expiresAt: string; title: string; owner: string }; history: Array<{ role: string; content: string; meta: string | null; createdAt: string }> }
  | { t: "turn"; message: string }
  | { t: "delta"; text: string }
  | { t: "tool"; tool: string; status: string; detail?: string }
  | { t: "file"; path: string; change: string }
  | { t: "steer"; by: string; text: string }
  | { t: "done"; verification: TurnVerificationReply | null }
  | { t: "error"; error: string }
  | { t: "truncated" }
  | { t: "end"; reason: string };

/**
 * Watch a teammate's shared session. The token travels in a POST body — never
 * a URL — and the stream ends when the share is revoked or expires.
 */
export function watchLiveSession(token: string, onEvent: (e: LiveViewEvent) => void): { done: Promise<void>; abort: () => void } {
  const controller = new AbortController();
  const done = (async (): Promise<void> => {
    const res = await fetch("/api/live/watch", {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ token }),
      signal: controller.signal,
    });
    if (res.status === 401) throw new AuthError();
    if (res.status !== 200 || !res.body) {
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(json.error ?? `could not open the live session (${String(res.status)})`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    try {
      for (;;) {
        const { value, done: streamDone } = await reader.read();
        if (streamDone) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() ?? "";
        for (const frame of frames) {
          const line = frame.split("\n").find((l) => l.startsWith("data: "));
          if (line) onEvent(JSON.parse(line.slice(6)) as LiveViewEvent);
        }
      }
    } catch (e) {
      void reader.cancel().catch(() => undefined); // see streamChat
      throw e;
    }
  })();
  return { done, abort: () => controller.abort() };
}

export async function steerLiveSession(token: string, text: string): Promise<boolean> {
  const r = await postJson<{ accepted: boolean }>("/api/live/steer", { token, text });
  return r.accepted;
}

export interface ProviderStatus {
  active: string;
  autoMode: boolean;
  resolvedActive: string;
  /** Tavsin supplies the key (server env) — user can build with no key of their own. */
  managed?: boolean;
  providers: Array<{ id: string; configured: boolean; hasKey: boolean; ownKey?: boolean; managed?: boolean; defaultModel: string | null; baseUrl: string | null }>;
}
export interface QuotaStatus {
  tier: "free" | "pro" | "team" | "enterprise";
  fastUsed: number;
  fastLimit: number | null;
  slowUsed: number;
  slowLimit: number | null;
  costUsed: number;
  costLimit: number | null;
  monthStartIso: string;
  monthEndIso: string;
  exceeded: boolean;
  byok: boolean;
  /** Included tokens (input + output) this month, and the plan's allowance. */
  tokensUsed?: number;
  tokensLimit?: number | null;
  /** Which allowance binds, if any (cost / outcomes / tokens / fast_requests / slow_requests). */
  limitReason?: string | null;
  /** Prepaid credits — never expire; pay for usage beyond the plan. */
  balanceUsd?: number;
  /** An allowance binds and credits are covering usage. */
  onCredits?: boolean;
  /** The prepaid balance in metered tokens (managed hosts). */
  balanceTokens?: number;
  /** Tokens are metered at a reference rate (a managed host), not counted raw. */
  metered?: boolean;
  meterUsdPerM?: number;
}

/** One model offered on the operator's keys (core managedCatalog.ts). */
export interface ManagedModelView {
  id: string;
  provider: string;
  vendor: string;
  label: string;
  class: "fast" | "balanced" | "frontier";
  /** How fast it draws the budget: 1 = one token per token; 0.05 = twenty per token. */
  multiplier: number | null;
  inPlan: boolean;
  available: boolean;
  inputPerM: number | null;
  outputPerM: number | null;
}

/** `models.managed` — managed inference for this user, or `managed: false`. */
export interface ManagedCatalog {
  managed: boolean;
  tier: "free" | "pro" | "team" | "enterprise";
  meterUsdPerM: number;
  creditTokensPerUsd: number;
  plans: { tier: "free" | "pro" | "team"; tokens: number | null }[];
  models: ManagedModelView[];
}

/** Start a Stripe checkout for a credit pack; resolves to the checkout URL. */
export async function buyCredits(usd: number): Promise<string> {
  const res = await fetch("/api/billing/credits", { method: "POST", headers: authHeaders({ "content-type": "application/json" }), body: JSON.stringify({ usd }) });
  if (res.status === 401) throw new AuthError();
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; data?: { url?: string }; error?: string };
  if (!body.ok || !body.data?.url) throw new Error(body.error ?? "could not start the credit checkout");
  return body.data.url;
}
export interface ConversationSummary {
  id: string;
  title: string;
  updatedAt: string;
  messageCount: number;
  /** First user message, truncated; null when the session has none yet. */
  preview: string | null;
}
export interface TurnDecisionView {
  step: "model" | "playbook" | "design" | "verify" | "approval";
  chose: string;
  rule: string | null;
  inputs: { label: string; value: string }[];
  reason: string;
  alternative: string | null;
}

/** Subset of desktop `TaskRatingView` — enough for a future Ratings panel. */
export interface TaskRatingView {
  id: string;
  traceId: string;
  raterScore: number | null;
  userScore: number | null;
  field: string;
  taskType: string;
  model: string;
  provider: string;
}

export interface MessageChangedFile {
  path: string;
  change: "created" | "modified";
  diff?: string;
}

/** One row from `conversations.get` (mirrors desktop `ChatMessageView`). */
export interface ConversationMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  meta: string | null;
  changedFiles?: MessageChangedFile[];
  traceId?: string | null;
  verification?: TurnVerificationReply | null;
  decisions?: TurnDecisionView[];
  policy?: TurnPolicyView;
  confidence?: TurnConfidenceView | null;
  questions?: SettledQuestionView[];
  rating?: TaskRatingView | null;
}

/** @deprecated Use ConversationMessage — kept for session export typing. */
export type ChatMessage = ConversationMessage;

export interface ConversationDetail {
  conversation: { id: string; title: string };
  messages: ConversationMessage[];
}
export interface ChangedFile {
  path: string;
  label: string;
}
export interface JobView {
  id: string;
  goal: string;
  status: "queued" | "running" | "completed" | "failed" | "cancelled";
  summary: string | null;
  error: string | null;
  traceId: string | null;
}
export interface SandboxView {
  jobId: string;
  goal: string;
  branch: string;
}
export interface TraceEvent {
  seq: number;
  summary: string;
}
export interface FileTreeNode {
  name: string;
  path: string;
  type: "dir" | "file";
  children?: FileTreeNode[];
}
export interface UsageReport {
  today: { costUsd: number };
  week?: { costUsd: number };
  month: { costUsd: number };
  monthlyBudgetUsd: number | null;
  weekResetAt?: string;
  monthResetAt?: string;
  compression?: { savedTokens: number; savedTokenPct: number };
}
